import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Category } from "./CategoryService";

// GraphQL queries
const ROOM_LIST_QUERY = gql`
  query RoomList($filter: RoomFilter, $params: PaginationAndSorting) {
    roomList(filter: $filter, params: $params) {
      code
      data {
        id
        venueId
        roomTypeId
        name
        roomNumber
        description
        capacity
        footageSqm
        floor
        categories {
          id
          name
        }
        locationNotes
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_GET_QUERY = gql`
  query RoomGet($id: ID) {
    roomGet(id: $id) {
      code
      data {
        id
        venueId
        roomTypeId
        name
        roomNumber
        description
        capacity
        footageSqm
        floor
        categories {
          id
          name
        }
        locationNotes
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_GET_MANY_QUERY = gql`
  query RoomGetMany($ids: [ID]) {
    roomGetMany(ids: $ids) {
      code
      data {
        id
        venueId
        roomTypeId
        name
        roomNumber
        description
        capacity
        footageSqm
        floor
        categories {
          id
          name
        }
        locationNotes
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const ROOM_CREATE_MUTATION = gql`
  mutation RoomCreate($params: RoomInput) {
    roomCreate(params: $params) {
      code
      data {
        id
        venueId
        roomTypeId
        name
        roomNumber
        description
        capacity
        footageSqm
        floor
        categories {
          id
          name
        }
        locationNotes
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_UPDATE_MUTATION = gql`
  mutation RoomUpdate($where: RoomWhereInput, $params: RoomInput) {
    roomUpdate(where: $where, params: $params) {
      code
      data {
        id
        venueId
        roomTypeId
        name
        roomNumber
        description
        capacity
        footageSqm
        floor
        categories {
          id
          name
        }
        locationNotes
        orderNo
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ROOM_REMOVE_MUTATION = gql`
  mutation RoomRemove($where: RoomWhereInput) {
    roomRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface Room {
  id?: string;
  venueId?: string | undefined;
  roomTypeId?: string;
  name?: string;
  roomNumber?: string;
  description?: string | undefined;
  capacity?: number | undefined;
  footageSqm?: number | undefined;
  floor?: string | undefined;
  categories?: Category[] | null | undefined;
  locationNotes?: string | undefined;
  orderNo?: number | undefined;
  status?: number;
}

export interface RoomInput {
  venueId?: string | null | undefined;
  roomTypeId?: string | null | undefined;
  name?: string | null | undefined;
  roomNumber?: string | null | undefined;
  description?: string | null | undefined;
  capacity?: number | null | undefined;
  footageSqm?: number | null | undefined;
  floor?: string | null | undefined;
  categories?: string[] | null | undefined;
  locationNotes?: string | null | undefined;
  orderNo?: number | null | undefined;
  status?: number | null | undefined;
}

export interface RoomFilter {
  id?: string[] | null | undefined;
  venueId?: string[] | null | undefined;
  roomTypeId?: string[] | null | undefined;
  name?: string | null | undefined;
  roomNumber?: string | null | undefined;
  floor?: string | null | undefined;
  categories?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface RoomWhereInput {
  id: string;
}

/**
 * Room service that handles all room-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove
 */
export class RoomService {
  /**
   * Get a list of rooms with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: RoomFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(ROOM_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single room by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(ROOM_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple rooms by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(ROOM_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new room
   */
  static async create(params: RoomInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(ROOM_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing room
   */
  static async update(id: string, params: RoomInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(ROOM_UPDATE_MUTATION, {
      where: { id },
      params,
    });
    return result;
  }

  /**
   * Remove/delete a room
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(ROOM_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }
}
